<script setup>
import { computed, ref, watch } from 'vue'
import { ArrowLeft, RefreshCw } from '@lucide/vue'
import {
  Badge,
  Button,
  EmptyState,
  Modal,
  PageHeader,
  Section,
  Spinner,
  Stat,
  Table,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { getPlayerInteractions } from '../api/analytics.js'
import { getUserBehaviorEvents, getUserDetail } from '../api/users.js'
import UserManagementActions from '../components/UserManagementActions.vue'
import { ledgerTypeVariant, orderStatusVariant } from './billing/format.js'

const props = defineProps({
  id: { type: String, required: true },
})

const { t, te } = useI18n()
const router = useRouter()
const detail = ref(null)
const behavior = ref(null)
const behaviorView = ref('visits')
const loading = ref(false)
const error = ref('')
const runModalOpen = ref(false)
const selectedRun = ref(null)
const runInteractions = ref(null)
const runInteractionsLoading = ref(false)
const runInteractionsError = ref('')

const ledgerColumns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.users.detail.ledger.time'), width: '180px' },
  { key: 'id', label: t('visualGameAdmin.users.detail.ledger.id'), width: '260px', mono: true },
  { key: 'type', label: t('visualGameAdmin.users.detail.ledger.type'), width: '100px' },
  { key: 'amount', label: t('visualGameAdmin.users.detail.ledger.amount'), width: '110px', align: 'right', mono: true },
  { key: 'balance_after', label: t('visualGameAdmin.users.detail.ledger.balance'), width: '120px', align: 'right', mono: true },
  { key: 'reason', label: t('visualGameAdmin.users.detail.ledger.reason'), width: '160px' },
  { key: 'reference', label: t('visualGameAdmin.users.detail.ledger.reference'), width: '240px' },
])

const orderColumns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.users.detail.orders.time'), width: '180px' },
  { key: 'id', label: t('visualGameAdmin.users.detail.orders.id'), width: '260px', mono: true },
  { key: 'product_id', label: t('visualGameAdmin.users.detail.orders.product'), width: '180px' },
  { key: 'mode', label: t('visualGameAdmin.users.detail.orders.mode'), width: '120px' },
  { key: 'provider', label: t('visualGameAdmin.users.detail.orders.provider'), width: '100px' },
  { key: 'status', label: t('visualGameAdmin.users.detail.orders.status'), width: '110px' },
  { key: 'amount', label: t('visualGameAdmin.users.detail.orders.amount'), width: '140px', align: 'right', mono: true },
  { key: 'refund', label: t('visualGameAdmin.users.detail.orders.refund'), width: '140px', align: 'right', mono: true },
])

const runColumns = computed(() => [
  { key: 'updated_at', label: t('visualGameAdmin.users.detail.runs.time'), width: '180px' },
  { key: 'id', label: t('visualGameAdmin.users.detail.runs.id'), width: '260px', mono: true },
  { key: 'scenario', label: t('visualGameAdmin.users.detail.runs.scenario'), width: '240px' },
  { key: 'status', label: t('visualGameAdmin.users.detail.runs.status'), width: '110px' },
  { key: 'turn_count', label: t('visualGameAdmin.users.detail.runs.turns'), width: '90px', align: 'right', mono: true },
  { key: 'phase', label: t('visualGameAdmin.users.detail.runs.phase'), width: '120px' },
  { key: 'location', label: t('visualGameAdmin.users.detail.runs.location'), width: '180px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '120px' },
])

const interactionColumns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.analytics.interactions.time'), width: '180px' },
  { key: 'turn_no', label: t('visualGameAdmin.users.detail.runs.interactions.turn'), width: '100px', align: 'right', mono: true },
  { key: 'mode', label: t('visualGameAdmin.analytics.interactions.mode'), width: '100px' },
  { key: 'input_text', label: t('visualGameAdmin.analytics.interactions.content'), width: '520px' },
])

const auditColumns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.users.detail.audit.time'), width: '180px' },
  { key: 'action', label: t('visualGameAdmin.users.detail.audit.action'), width: '180px' },
  { key: 'operator_id', label: t('visualGameAdmin.users.detail.audit.operator'), width: '260px', mono: true },
  { key: 'reason', label: t('visualGameAdmin.users.detail.audit.reason'), width: '320px' },
])

const behaviorColumns = computed(() => [
  { key: 'occurred_at', label: t('visualGameAdmin.users.detail.behavior.time'), width: '180px' },
  { key: 'event_name', label: t('visualGameAdmin.users.detail.behavior.event'), width: '220px' },
  { key: 'page_path', label: t('visualGameAdmin.users.detail.behavior.page'), width: '260px' },
  { key: 'engagement_ms', label: t('visualGameAdmin.users.detail.behavior.duration'), width: '140px', align: 'right', mono: true },
  { key: 'metadata', label: t('visualGameAdmin.users.detail.behavior.details'), width: '420px' },
])

const behaviorDays = computed(() => {
  const groups = new Map()
  for (const event of behavior.value?.items || []) {
    const occurredAt = new Date(event.occurred_at)
    const dayKey = [
      occurredAt.getFullYear(),
      String(occurredAt.getMonth() + 1).padStart(2, '0'),
      String(occurredAt.getDate()).padStart(2, '0'),
    ].join('-')
    if (!groups.has(dayKey)) {
      groups.set(dayKey, {
        key: dayKey,
        label: occurredAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' }),
        items: [],
        engagement_ms: 0,
        session_ids: new Set(),
      })
    }
    const group = groups.get(dayKey)
    group.items.push(event)
    group.engagement_ms += event.engagement_ms || 0
    group.session_ids.add(event.session_id)
  }
  return [...groups.values()].map(group => ({
    ...group,
    session_count: group.session_ids.size,
  }))
})

const behaviorVisitDays = computed(() => {
  const groups = new Map()
  for (const visit of behavior.value?.visits || []) {
    const startedAt = new Date(visit.started_at)
    const dayKey = [startedAt.getFullYear(), String(startedAt.getMonth() + 1).padStart(2, '0'), String(startedAt.getDate()).padStart(2, '0')].join('-')
    if (!groups.has(dayKey)) {
      groups.set(dayKey, {
        key: dayKey,
        label: startedAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' }),
        visits: [],
        devices: new Set(),
        locations: new Set(),
        sources: new Set(),
      })
    }
    const group = groups.get(dayKey)
    group.visits.push(visit)
    for (const event of visit.events) appendDayVisitorAttributes(group, event.metadata || {})
  }
  return [...groups.values()].map(group => ({
    ...group,
    device_label: joinedAttribute(group.devices, t('visualGameAdmin.users.detail.behavior.unknownDevice')),
    location_label: joinedAttribute(group.locations, t('visualGameAdmin.users.detail.behavior.unknownLocation')),
    source_label: joinedAttribute(group.sources, t('visualGameAdmin.users.detail.behavior.unknownSource')),
  }))
})

function appendDayVisitorAttributes(group, metadata) {
  const deviceType = metadata.device_type ? behaviorValueLabel('deviceTypes', metadata.device_type) : ''
  const platform = metadata.device_platform && metadata.device_platform !== 'unknown' ? metadata.device_platform : ''
  if (metadata.device_type || platform) group.devices.add([deviceType, platform].filter(Boolean).join(' · '))

  const location = [metadata.country, metadata.region].filter(Boolean).join(' · ')
  if (location) group.locations.add(location)

  if (metadata.traffic_source && metadata.traffic_source !== 'internal') {
    const source = behaviorValueLabel('trafficSources', metadata.traffic_source)
    const medium = behaviorValueLabel('trafficMediums', metadata.traffic_medium)
    group.sources.add(metadata.traffic_medium && metadata.traffic_medium !== 'none' ? `${source} · ${medium}` : source)
  }
}

function joinedAttribute(values, fallback) {
  return values.size === 0 ? fallback : [...values].join(' / ')
}

watch(() => props.id, loadDetail, { immediate: true })

async function loadDetail() {
  loading.value = true
  error.value = ''
  try {
    const [response, behaviorResponse] = await Promise.all([
      getUserDetail(props.id),
      getUserBehaviorEvents(props.id),
    ])
    detail.value = response.detail
    behavior.value = behaviorResponse
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function backToUsers() {
  router.push({ name: 'users' })
}

function replaceDetail(nextDetail) {
  detail.value = nextDetail
}

async function openRunInteractions(run) {
  selectedRun.value = run
  runModalOpen.value = true
  runInteractions.value = null
  runInteractionsLoading.value = true
  runInteractionsError.value = ''
  try {
    const since = new Date(run.created_at)
    const until = new Date()
    until.setDate(until.getDate() + 1)
    runInteractions.value = await getPlayerInteractions(props.id, {
      since: since.toISOString(),
      until: until.toISOString(),
      run_id: run.id,
      limit: 100,
    })
  } catch (requestError) {
    runInteractionsError.value = requestError.message
  } finally {
    runInteractionsLoading.value = false
  }
}

function closeRunInteractions() {
  runModalOpen.value = false
  selectedRun.value = null
  runInteractions.value = null
  runInteractionsError.value = ''
}

function interactionMode(value) {
  return t(`visualGameAdmin.analytics.interactions.modes.${value}`)
}

function formatOptional(value) {
  return value === null ? '—' : value
}

function formatDate(value) {
  return value === null ? '—' : new Date(value).toLocaleString()
}

function formatDuration(milliseconds) {
  if (!milliseconds) return '—'
  const seconds = Math.round(milliseconds / 1000)
  return t('visualGameAdmin.users.detail.behavior.durationValue', {
    minutes: Math.floor(seconds / 60),
    seconds: seconds % 60,
  })
}

function formatVisitTime(visit) {
  return `${new Date(visit.started_at).toLocaleTimeString()} – ${new Date(visit.ended_at).toLocaleTimeString()}`
}

function behaviorEventLabel(event) {
  if (event.event_name === 'ui_click') {
    return t('visualGameAdmin.users.detail.behavior.clickEventName', {
      target: behaviorControlLabel(event.metadata?.element_id),
    })
  }
  if (event.event_name === 'action_result') {
    return t('visualGameAdmin.users.detail.behavior.resultEventName', {
      action: behaviorControlLabel(event.metadata?.action_id),
    })
  }
  const key = `visualGameAdmin.users.detail.behavior.eventNames.${event.event_name}`
  return te(key) ? t(key) : event.event_name
}

function behaviorPageLabel(event) {
  const path = event.page_path || '/'
  const exactPages = {
    '/': 'home',
    '/landing': 'home',
    '/stories': 'stories',
    '/dramas': 'dramas',
    '/search': 'search',
    '/login': 'login',
    '/game': 'game',
    '/story-tree': 'storyTree',
    '/account': 'account',
    '/account/edit': 'accountEdit',
    '/account/settings': 'accountSettings',
    '/my-games': 'myGames',
    '/my-videos': 'myVideos',
    '/my-favorites': 'myFavorites',
    '/video-generation': 'videoGeneration',
    '/create': 'create',
    '/billing': 'billing',
    '/credit': 'credit',
    '/waitlist': 'waitlist',
  }
  let pageKey = exactPages[path]
  if (!pageKey && path.startsWith('/story/')) pageKey = 'storyDetail'
  if (!pageKey && path.startsWith('/players/')) pageKey = 'playerProfile'
  if (pageKey) return t(`visualGameAdmin.users.detail.behavior.pageNames.${pageKey}`)
  return event.page_title || path
}

function behaviorDetail(event) {
  const metadata = event.metadata || {}
  switch (event.event_name) {
    case 'page_view':
      return t('visualGameAdmin.users.detail.behavior.descriptions.pageView', { page: behaviorPageLabel(event) })
    case 'page_engagement':
      return t('visualGameAdmin.users.detail.behavior.descriptions.pageEngagement', {
        page: behaviorPageLabel(event),
        duration: formatDuration(event.engagement_ms),
      })
    case 'game_start':
      if (event.scenario_title) {
        return t('visualGameAdmin.users.detail.behavior.descriptions.gameStartNamed', {
          title: event.scenario_title,
          id: metadata.scenario_id,
        })
      }
      return metadata.scenario_id
        ? t('visualGameAdmin.users.detail.behavior.descriptions.gameStart', { id: metadata.scenario_id })
        : t('visualGameAdmin.users.detail.behavior.descriptions.gameStartGeneric')
    case 'turn_submit':
      return t(event.scenario_title
        ? 'visualGameAdmin.users.detail.behavior.descriptions.turnSubmitNamed'
        : 'visualGameAdmin.users.detail.behavior.descriptions.turnSubmit', {
        title: event.scenario_title,
        method: behaviorValueLabel('inputMethods', metadata.input_method),
      })
    case 'ending_reached':
      if (event.scenario_title) {
        return t('visualGameAdmin.users.detail.behavior.descriptions.endingReachedNamed', {
          title: event.scenario_title,
          id: metadata.ending_id || '—',
        })
      }
      return metadata.ending_id
        ? t('visualGameAdmin.users.detail.behavior.descriptions.endingReached', { id: metadata.ending_id })
        : t('visualGameAdmin.users.detail.behavior.descriptions.endingReachedGeneric')
    case 'vg_video_start':
      return t('visualGameAdmin.users.detail.behavior.descriptions.videoStart', {
        video: behaviorVideoName(event),
        id: metadata.video_id || '—',
      })
    case 'vg_video_progress':
      return t('visualGameAdmin.users.detail.behavior.descriptions.videoProgress', {
        video: behaviorVideoName(event),
        id: metadata.video_id || '—',
        percent: metadata.video_percent || '—',
      })
    case 'vg_video_error':
      return t('visualGameAdmin.users.detail.behavior.descriptions.videoError', {
        video: behaviorVideoName(event),
        id: metadata.video_id || '—',
        code: metadata.error_code || '—',
      })
    case 'vg_video_complete':
      return t('visualGameAdmin.users.detail.behavior.descriptions.videoComplete', {
        video: behaviorVideoName(event),
        id: metadata.video_id || '—',
      })
    case 'ui_click':
      if (metadata.element_id === 'story_level' && metadata.scenario_id) {
        return t('visualGameAdmin.users.detail.behavior.descriptions.storyLevelClick', {
          title: event.scenario_title || metadata.scenario_id,
          id: metadata.scenario_id,
        })
      }
      return t('visualGameAdmin.users.detail.behavior.descriptions.uiClick', {
        target: behaviorControlLabel(metadata.element_id),
        surface: behaviorSurfaceLabel(metadata.surface),
      })
    case 'action_result':
      return t('visualGameAdmin.users.detail.behavior.descriptions.actionResult', {
        action: behaviorControlLabel(metadata.action_id),
        result: behaviorValueLabel('actionResults', metadata.result),
      })
    case 'scene_click':
      return t('visualGameAdmin.users.detail.behavior.descriptions.sceneClick', {
        result: behaviorValueLabel('interactionResults', metadata.interaction_result),
      })
    case 'login':
    case 'sign_up':
      return t('visualGameAdmin.users.detail.behavior.descriptions.auth', {
        method: behaviorValueLabel('authMethods', metadata.method),
      })
    case 'content_click':
      return t('visualGameAdmin.users.detail.behavior.descriptions.contentClick', {
        surface: behaviorValueLabel('contentSurfaces', metadata.surface),
        type: behaviorValueLabel('contentTypes', metadata.content_type),
        title: metadata.content_title || metadata.content_id || t('visualGameAdmin.users.detail.behavior.unknown'),
        position: metadata.position || '—',
        destination: metadata.destination || '—',
      })
    default:
      return formatBehaviorMetadata(metadata)
  }
}

function behaviorVideoName(event) {
  return event.video_title || t('visualGameAdmin.users.detail.behavior.unknownVideo')
}

function isVideoEvent(event) {
  return ['vg_video_start', 'vg_video_progress', 'vg_video_complete', 'vg_video_error'].includes(event.event_name)
}

function behaviorValueLabel(group, value) {
  if (!value) return t('visualGameAdmin.users.detail.behavior.unknown')
  const key = `visualGameAdmin.users.detail.behavior.${group}.${value}`
  return te(key) ? t(key) : value
}

function behaviorControlLabel(value) {
  if (!value) return t('visualGameAdmin.users.detail.behavior.unknownControl')
  const key = `visualGameAdmin.users.detail.behavior.controlLabels.${value}`
  if (te(key)) return t(key)
  if (value.startsWith('auth_')) {
    return t('visualGameAdmin.users.detail.behavior.oauthControl', {
      method: behaviorValueLabel('authMethods', value.slice('auth_'.length)),
    })
  }
  return t('visualGameAdmin.users.detail.behavior.unknownControl')
}

function behaviorSurfaceLabel(value) {
  if (!value) return t('visualGameAdmin.users.detail.behavior.unknownArea')
  const key = `visualGameAdmin.users.detail.behavior.surfaceLabels.${value}`
  return te(key) ? t(key) : t('visualGameAdmin.users.detail.behavior.unknownArea')
}

function formatBehaviorMetadata(metadata = {}, additionalIgnoredKeys = []) {
  const ignoredKeys = new Set(['app_name', 'environment', 'app_version', 'locale', 'auth_state', 'page_type', 'page_instance_id', ...additionalIgnoredKeys])
  const entries = Object.entries(metadata).filter(([key, value]) => !ignoredKeys.has(key) && value !== null && value !== '')
  return entries.length === 0
    ? t('visualGameAdmin.users.detail.behavior.noAdditionalDetails')
    : entries.map(([key, value]) => `${key}: ${value}`).join(' · ')
}

function formatIdentity(user) {
  return user.email === null ? user.id : user.email
}

function formatReference(entry) {
  if (entry.ref_type === null) return '—'
  return entry.ref_id === null ? entry.ref_type : `${entry.ref_type} · ${entry.ref_id}`
}

function formatScenario(run) {
  return run.scenario_title === null ? run.scenario_id : run.scenario_title
}

function formatOrderAmount(order) {
  if (order.amount_cents === null) return '—'
  if (order.currency === null) return String(order.amount_cents)
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: order.currency.toUpperCase(),
  }).format(order.amount_cents / 100)
}

function formatRefundAmount(order) {
  if (order.refund_amount === null) return '—'
  if (order.currency === null) return String(order.refund_amount)
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: order.currency.toUpperCase(),
  }).format(order.refund_amount / 100)
}

function accountTypeVariant(accountType) {
  switch (accountType) {
    case 'registered':
      return 'success'
    case 'guest':
      return 'info'
    default:
      throw new Error(`Unhandled account type: ${accountType}, id: ${props.id}`)
  }
}

function subscriptionVariant(status) {
  switch (status) {
    case 'active':
      return 'success'
    case 'expired':
      return 'warning'
    case 'canceled':
    case 'none':
      return 'neutral'
    default:
      throw new Error(`Unhandled subscription status: ${status}, id: ${props.id}`)
  }
}

function runVariant(status) {
  switch (status) {
    case 'active':
      return 'info'
    case 'completed':
      return 'success'
    default:
      throw new Error(`Unhandled run status: ${status}, id: ${props.id}`)
  }
}
</script>

<template>
  <section class="user-detail vga-page">
    <PageHeader :title="t('visualGameAdmin.users.detail.title')">
      <template #actions>
        <Button variant="ghost" @click="backToUsers">
          <ArrowLeft :size="16" />
          {{ t('visualGameAdmin.users.detail.back') }}
        </Button>
        <Button variant="secondary" :loading="loading" @click="loadDetail">
          <RefreshCw :size="16" />
          {{ t('visualGameAdmin.users.detail.refresh') }}
        </Button>
        <UserManagementActions v-if="detail !== null" :detail="detail" @updated="replaceDetail" />
      </template>
    </PageHeader>

    <div v-if="loading && detail === null" class="vga-state">
      <Spinner />
      <span>{{ t('visualGameAdmin.common.loading') }}</span>
    </div>

    <div v-else-if="error" class="vga-state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="loadDetail">
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </div>

    <template v-else-if="detail !== null">
      <Section :title="t('visualGameAdmin.users.detail.profile')" data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#profile">
        <div class="user-detail__profile">
          <div class="user-detail__identity">
            <strong>{{ formatIdentity(detail.user) }}</strong>
            <Badge :variant="accountTypeVariant(detail.user.account_type)">
              {{ t(`visualGameAdmin.users.type.${detail.user.account_type}`) }}
            </Badge>
          </div>
          <dl class="user-detail__fields">
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.id') }}</dt><dd>{{ detail.user.id }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.displayName') }}</dt><dd>{{ formatOptional(detail.user.display_name) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.nickname') }}</dt><dd>{{ formatOptional(detail.user.nickname) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.avatar') }}</dt><dd>{{ formatOptional(detail.user.avatar_url) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.accountLocale') }}</dt><dd>{{ formatOptional(detail.user.account_locale) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.profileLocale') }}</dt><dd>{{ formatOptional(detail.user.profile_locale) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.emailVerified') }}</dt><dd>{{ formatDate(detail.user.email_verified_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.fields.createdAt') }}</dt><dd>{{ formatDate(detail.user.created_at) }}</dd></div>
          </dl>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.users.detail.overview')" data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#overview">
        <div class="user-detail__stats">
          <Stat :label="t('visualGameAdmin.users.detail.metrics.credits')" :value="detail.credits.balance" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.runs')" :value="detail.play.summary.runs" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.activeRuns')" :value="detail.play.summary.active_runs" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.completedRuns')" :value="detail.play.summary.completed_runs" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.turns')" :value="detail.play.summary.turns" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.sayTurns')" :value="detail.play.summary.say_turns" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.actTurns')" :value="detail.play.summary.act_turns" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.creditsSpent')" :value="detail.play.summary.credits_spent" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.creditsGranted')" :value="detail.play.summary.credits_granted" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.completedSeasons')" :value="detail.play.summary.completed_seasons" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.scenarios')" :value="detail.play.summary.scenarios" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.progress')" :value="detail.play.summary.progress_records" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.endings')" :value="detail.play.summary.endings" />
          <Stat :label="t('visualGameAdmin.users.detail.metrics.saves')" :value="detail.play.summary.save_slots" />
        </div>
        <dl class="user-detail__fields user-detail__fields--subscription">
          <div>
            <dt>{{ t('visualGameAdmin.users.detail.subscription.status') }}</dt>
            <dd>
              <Badge :variant="subscriptionVariant(detail.subscription.status)">
                {{ t(`visualGameAdmin.users.subscriptionStatus.${detail.subscription.status}`) }}
              </Badge>
            </dd>
          </div>
          <div><dt>{{ t('visualGameAdmin.users.detail.subscription.level') }}</dt><dd>{{ detail.subscription.level }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.users.detail.subscription.expiresAt') }}</dt><dd>{{ formatDate(detail.subscription.expires_at) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.users.detail.subscription.provider') }}</dt><dd>{{ formatOptional(detail.subscription.provider) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.users.detail.subscription.source') }}</dt><dd>{{ formatOptional(detail.subscription.source) }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.users.detail.metrics.lastActive') }}</dt><dd>{{ formatDate(detail.play.summary.last_active_at) }}</dd></div>
        </dl>
      </Section>

      <Section
        v-if="detail.user.account_type === 'guest'"
        :title="t('visualGameAdmin.users.detail.guestDevices.title')"
        data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#guest-devices"
      >
        <div class="user-detail__device-list">
          <dl v-for="(device, index) in detail.guest_devices" :key="index" class="user-detail__device">
            <div><dt>{{ t('visualGameAdmin.users.detail.guestDevices.status') }}</dt><dd>{{ t(`visualGameAdmin.users.detail.guestDevices.statuses.${device.status}`) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.guestDevices.lastSeen') }}</dt><dd>{{ formatDate(device.last_seen_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.users.detail.guestDevices.mergedUser') }}</dt><dd>{{ formatOptional(device.merged_user_id) }}</dd></div>
          </dl>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.users.detail.behavior.title')" :padded="false">
        <div v-if="behavior !== null" class="user-detail__behavior-summary">
          <Stat :label="t('visualGameAdmin.users.detail.behavior.sessions')" :value="behavior.session_count" />
          <Stat :label="t('visualGameAdmin.users.detail.behavior.events')" :value="behavior.event_count" />
          <Stat :label="t('visualGameAdmin.users.detail.behavior.engagement')" :value="formatDuration(behavior.engagement_ms)" />
        </div>
        <p v-if="behavior !== null && behavior.items.length > 0" class="user-detail__behavior-help">
          {{ t('visualGameAdmin.users.detail.behavior.help') }}
        </p>
        <div v-if="behavior !== null && behavior.items.length > 0" class="user-detail__behavior-view-switch" role="group" :aria-label="t('visualGameAdmin.users.detail.behavior.viewMode')">
          <Button :variant="behaviorView === 'visits' ? 'primary' : 'ghost'" size="sm" @click="behaviorView = 'visits'">
            {{ t('visualGameAdmin.users.detail.behavior.pageVisits') }}
          </Button>
          <Button :variant="behaviorView === 'events' ? 'primary' : 'ghost'" size="sm" @click="behaviorView = 'events'">
            {{ t('visualGameAdmin.users.detail.behavior.rawEvents') }}
          </Button>
        </div>
        <EmptyState v-if="behavior === null || behavior.items.length === 0" :title="t('visualGameAdmin.users.detail.behavior.empty')" />
        <div v-else-if="behaviorView === 'visits'" class="user-detail__behavior-days">
          <section v-for="day in behaviorVisitDays" :key="day.key" class="user-detail__visit-day">
            <header class="user-detail__visit-day-heading">
              <h3>{{ day.label }}</h3>
              <span>{{ t('visualGameAdmin.users.detail.behavior.dayDevice', { value: day.device_label }) }}</span>
              <span>{{ t('visualGameAdmin.users.detail.behavior.dayLocation', { value: day.location_label }) }}</span>
              <span>{{ t('visualGameAdmin.users.detail.behavior.daySource', { value: day.source_label }) }}</span>
            </header>
            <details v-for="(visit, index) in day.visits" :key="visit.visit_id" class="user-detail__behavior-day" :open="index === 0">
              <summary class="user-detail__visit-summary">
                <span class="user-detail__visit-time">{{ formatVisitTime(visit) }}</span>
                <span class="user-detail__visit-page"><strong>{{ behaviorPageLabel(visit) }}</strong><code>{{ visit.page_path || '/' }}</code></span>
                <span>{{ visit.operation_count }} {{ t('visualGameAdmin.users.detail.behavior.operations') }}</span>
                <span>{{ t('visualGameAdmin.users.detail.behavior.engagement') }} {{ formatDuration(visit.engagement_ms) }}</span>
                <Badge :variant="visit.grouping === 'exact' ? 'success' : 'neutral'">{{ t(`visualGameAdmin.users.detail.behavior.grouping.${visit.grouping}`) }}</Badge>
              </summary>
              <ol class="user-detail__visit-events">
                <li v-for="event in visit.events" :key="event.id">
                  <time>{{ new Date(event.occurred_at).toLocaleTimeString() }}</time>
                  <div>
                    <strong>{{ behaviorEventLabel(event) }}</strong>
                    <span>{{ behaviorDetail(event) }}</span>
                    <a v-if="isVideoEvent(event) && event.video_url" class="user-detail__behavior-video-link" :href="event.video_url" target="_blank" rel="noreferrer">{{ t('visualGameAdmin.users.detail.behavior.openVideo') }}</a>
                  </div>
                </li>
              </ol>
            </details>
          </section>
        </div>
        <div v-else class="user-detail__behavior-days">
          <details
            v-for="(day, index) in behaviorDays"
            :key="day.key"
            class="user-detail__behavior-day"
            :open="index === 0"
          >
            <summary class="user-detail__behavior-day-summary">
              <strong>{{ day.label }}</strong>
              <span>{{ day.items.length }} {{ t('visualGameAdmin.users.detail.behavior.events') }}</span>
              <span>{{ day.session_count }} {{ t('visualGameAdmin.users.detail.behavior.sessions') }}</span>
              <span>{{ t('visualGameAdmin.users.detail.behavior.engagement') }} {{ formatDuration(day.engagement_ms) }}</span>
            </summary>
            <Table :columns="behaviorColumns" :rows="day.items">
              <template #occurred_at="{ row }">{{ formatDate(row.occurred_at) }}</template>
              <template #event_name="{ row }">
                <div class="user-detail__behavior-primary">{{ behaviorEventLabel(row) }}</div>
                <code class="user-detail__behavior-technical">{{ row.event_name }}</code>
              </template>
              <template #page_path="{ row }">
                <div class="user-detail__behavior-primary">{{ behaviorPageLabel(row) }}</div>
                <code class="user-detail__behavior-technical">{{ row.page_path || '/' }}</code>
              </template>
              <template #engagement_ms="{ row }">{{ formatDuration(row.engagement_ms) }}</template>
              <template #metadata="{ row }">
                <div class="user-detail__behavior-detail">
                  <span class="user-detail__behavior-description">{{ behaviorDetail(row) }}</span>
                  <a v-if="isVideoEvent(row) && row.video_url" class="user-detail__behavior-video-link" :href="row.video_url" target="_blank" rel="noreferrer">
                    {{ t('visualGameAdmin.users.detail.behavior.openVideo') }}
                  </a>
                </div>
              </template>
            </Table>
          </details>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.users.detail.ledger.title')" :padded="false" data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#ledger">
        <EmptyState v-if="detail.credits.ledger.length === 0" :title="t('visualGameAdmin.users.detail.ledger.empty')" />
        <Table v-else :columns="ledgerColumns" :rows="detail.credits.ledger">
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #type="{ row }"><Badge :variant="ledgerTypeVariant(row.type)">{{ t(`visualGameAdmin.users.detail.ledger.types.${row.type}`) }}</Badge></template>
          <template #amount="{ row }">{{ row.amount > 0 ? `+${row.amount}` : row.amount }}</template>
          <template #reason="{ row }">{{ formatOptional(row.reason) }}</template>
          <template #reference="{ row }">{{ formatReference(row) }}</template>
        </Table>
      </Section>

      <Section :title="t('visualGameAdmin.users.detail.audit.title')" :padded="false" data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#audit">
        <EmptyState v-if="detail.audit_logs.length === 0" :title="t('visualGameAdmin.users.detail.audit.empty')" />
        <Table v-else :columns="auditColumns" :rows="detail.audit_logs">
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #action="{ row }">{{ t(`visualGameAdmin.users.detail.audit.actions.${row.action}`) }}</template>
        </Table>
      </Section>

      <Section :title="t('visualGameAdmin.users.detail.orders.title')" :padded="false" data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#orders">
        <EmptyState v-if="detail.orders.length === 0" :title="t('visualGameAdmin.users.detail.orders.empty')" />
        <Table v-else :columns="orderColumns" :rows="detail.orders">
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #mode="{ row }">{{ t(`visualGameAdmin.users.detail.orders.modes.${row.mode}`) }}</template>
          <template #status="{ row }"><Badge :variant="orderStatusVariant(row.status)">{{ t(`visualGameAdmin.users.detail.orders.statuses.${row.status}`) }}</Badge></template>
          <template #amount="{ row }">{{ formatOrderAmount(row) }}</template>
          <template #refund="{ row }">{{ formatRefundAmount(row) }}</template>
        </Table>
      </Section>

      <Section :title="t('visualGameAdmin.users.detail.runs.title')" :padded="false" data-insp-path="apps/visual-game-admin/src/pages/UserDetail.vue#runs">
        <EmptyState v-if="detail.play.recent_runs.length === 0" :title="t('visualGameAdmin.users.detail.runs.empty')" />
        <Table v-else :columns="runColumns" :rows="detail.play.recent_runs">
          <template #updated_at="{ row }">{{ formatDate(row.updated_at === null ? row.created_at : row.updated_at) }}</template>
          <template #scenario="{ row }">{{ formatScenario(row) }}</template>
          <template #status="{ row }"><Badge :variant="runVariant(row.status)">{{ t(`visualGameAdmin.users.detail.runs.statuses.${row.status}`) }}</Badge></template>
          <template #phase="{ row }">{{ formatOptional(row.phase) }}</template>
          <template #location="{ row }">{{ formatOptional(row.current_location_id) }}</template>
          <template #actions="{ row }"><Button variant="ghost" size="sm" @click="openRunInteractions(row)">{{ t('visualGameAdmin.users.detail.runs.viewInteractions') }}</Button></template>
        </Table>
      </Section>
    </template>

    <Modal
      v-model:open="runModalOpen"
      :title="t('visualGameAdmin.users.detail.runs.interactions.title', { scenario: selectedRun === null ? '' : formatScenario(selectedRun) })"
      width="1120px"
      @close="closeRunInteractions"
    >
      <div v-if="runInteractionsLoading" class="vga-state"><Spinner /></div>
      <div v-else-if="runInteractionsError" class="vga-state"><p class="vga-error">{{ runInteractionsError }}</p></div>
      <EmptyState v-else-if="runInteractions === null || runInteractions.items.length === 0" :title="t('visualGameAdmin.users.detail.runs.interactions.empty')" />
      <template v-else>
        <p class="user-detail__interaction-summary">{{ t('visualGameAdmin.users.detail.runs.interactions.summary', { total: runInteractions.total }) }}</p>
        <Table :columns="interactionColumns" :rows="runInteractions.items">
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #mode="{ row }">{{ interactionMode(row.mode) }}</template>
          <template #input_text="{ row }"><div class="user-detail__interaction-content">{{ row.input_text }}</div></template>
        </Table>
      </template>
    </Modal>
  </section>
</template>

<style scoped>
.user-detail__profile,
.user-detail__device-list {
  display: grid;
  gap: var(--drama-space-xl);
}

.user-detail__interaction-summary {
  margin: 0 0 var(--drama-space-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.user-detail__behavior-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--drama-space-md);
  padding: var(--drama-space-lg);
}

.user-detail__behavior-help {
  margin: 0;
  padding: 0 var(--drama-space-lg) var(--drama-space-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.user-detail__behavior-days {
  display: grid;
  gap: var(--drama-space-sm);
  padding: 0 var(--drama-space-lg) var(--drama-space-lg);
}

.user-detail__behavior-view-switch {
  display: flex;
  gap: var(--drama-space-xs);
  padding: 0 var(--drama-space-lg) var(--drama-space-md);
}

.user-detail__visit-day {
  display: grid;
  gap: var(--drama-space-sm);
}

.user-detail__visit-day-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  margin: var(--drama-space-sm) 0 0;
}

.user-detail__visit-day-heading h3 {
  margin: 0 var(--drama-space-sm) 0 0;
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-md);
}

.user-detail__visit-day-heading span {
  padding: 3px var(--drama-space-sm);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.user-detail__visit-summary {
  display: grid;
  grid-template-columns: 170px minmax(240px, 1fr) auto auto auto;
  align-items: center;
  gap: var(--drama-space-md);
  padding: var(--drama-space-md) var(--drama-space-lg);
  color: var(--drama-color-text-muted);
  cursor: pointer;
}

.user-detail__visit-time {
  color: var(--drama-color-text-secondary);
  font-variant-numeric: tabular-nums;
}

.user-detail__visit-page {
  display: grid;
  gap: 2px;
}

.user-detail__visit-page strong { color: var(--drama-color-text); }
.user-detail__visit-page code { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }

.user-detail__visit-events {
  display: grid;
  margin: 0;
  padding: var(--drama-space-sm) var(--drama-space-lg) var(--drama-space-md) 210px;
  list-style: none;
}

.user-detail__visit-events li {
  position: relative;
  display: grid;
  grid-template-columns: 100px minmax(0, 1fr);
  gap: var(--drama-space-md);
  padding: var(--drama-space-sm) 0;
  border-left: 1px solid var(--drama-color-border);
}

.user-detail__visit-events li::before {
  position: absolute;
  top: 18px;
  left: -4px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--drama-color-primary);
  content: '';
}

.user-detail__visit-events time { padding-left: var(--drama-space-md); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); font-variant-numeric: tabular-nums; }
.user-detail__visit-events div { display: grid; justify-items: start; gap: 2px; }
.user-detail__visit-events span { color: var(--drama-color-text-secondary); font-size: var(--drama-font-size-sm); }

@media (max-width: 960px) {
  .user-detail__visit-summary { grid-template-columns: 1fr auto; }
  .user-detail__visit-page { grid-column: 1 / -1; grid-row: 1; }
  .user-detail__visit-events { padding-left: var(--drama-space-lg); }
}

.user-detail__behavior-day {
  overflow: hidden;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
}

.user-detail__behavior-day-summary {
  display: flex;
  align-items: center;
  gap: var(--drama-space-lg);
  padding: var(--drama-space-md) var(--drama-space-lg);
  color: var(--drama-color-text-muted);
  cursor: pointer;
  user-select: none;
}

.user-detail__behavior-day-summary strong {
  margin-right: auto;
  color: var(--drama-color-text);
}

.user-detail__behavior-day[open] .user-detail__behavior-day-summary {
  border-bottom: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface-subtle);
}

.user-detail__behavior-primary {
  color: var(--drama-color-text);
  font-weight: 600;
}

.user-detail__behavior-technical {
  display: block;
  margin-top: 2px;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.user-detail__behavior-description {
  color: var(--drama-color-text-secondary);
  line-height: var(--drama-line-height-normal);
  white-space: normal;
}

.user-detail__behavior-detail {
  display: grid;
  justify-items: start;
  gap: var(--drama-space-xs);
}

.user-detail__behavior-video-link {
  color: var(--drama-color-primary);
  font-size: var(--drama-font-size-sm);
  text-decoration: none;
}

.user-detail__behavior-video-link:hover {
  text-decoration: underline;
}

.user-detail__interaction-content {
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: var(--drama-line-height-normal);
}

.user-detail__identity {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  min-width: 0;
}

.user-detail__identity strong {
  overflow-wrap: anywhere;
}

.user-detail__fields,
.user-detail__device {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--drama-space-lg);
  margin: 0;
}

.user-detail__fields--subscription {
  margin-top: var(--drama-space-xl);
  padding-top: var(--drama-space-xl);
  border-top: 1px solid var(--drama-color-border);
}

.user-detail__fields div,
.user-detail__device div {
  min-width: 0;
}

.user-detail__fields dt,
.user-detail__device dt {
  margin-bottom: var(--drama-space-xs);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.user-detail__fields dd,
.user-detail__device dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.user-detail__stats {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--drama-space-md);
}

.user-detail__device {
  padding-bottom: var(--drama-space-lg);
  border-bottom: 1px solid var(--drama-color-border);
}

.user-detail__device:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

@media (max-width: 1100px) {
  .user-detail__stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .user-detail__fields,
  .user-detail__device {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .user-detail__stats,
  .user-detail__fields,
  .user-detail__device {
    grid-template-columns: 1fr;
  }

  .user-detail__behavior-day-summary {
    flex-wrap: wrap;
    gap: var(--drama-space-sm) var(--drama-space-md);
  }

  .user-detail__behavior-day-summary strong {
    flex-basis: 100%;
  }
}
</style>
